/**
 * AXOIX Vertical Tier Limits & Features Engine
 *
 * Single source of truth on the client for vertical-specific tier limits and feature copy.
 * Matches the exact contract defined in Mothership Backreport BR-138 (vertical_pricing.vertical_limits).
 *
 * Contract:
 * - If the Mothership API provides `vertical_limits` in `/api/pricing/verticals`, live data is used.
 * - If Mothership has not yet populated or deployed `vertical_limits`, standardized 20-vertical fallback
 *   catalog is used so that non-PG verticals (Healthcare, Education, Salon, Gym, etc.) immediately display
 *   their domain-accurate capacities (patients, students, chairs, tables, etc.) instead of generic user limits
 *   or leaked PG bed counts.
 * - On the general `/pricing` page (no vertical), clean business OS limits are rendered without bed leaks.
 * - On PG/Hotel pages, beds, rooms, properties, and overage rates (+₹75/bed) are preserved.
 */

import { PricingFeature, PricingTier } from '../hooks/usePricingData';

export interface VerticalMetric {
  key: string;
  label: string;
  unit: string;
  overage_key?: string;
}

export interface VerticalTierData {
  feature_bullets: string[];
  [key: string]: any;
}

export interface VerticalLimitsSchema {
  metrics: VerticalMetric[];
  tiers: {
    explorer: VerticalTierData;
    officer: VerticalTierData;
    commander: VerticalTierData;
    titan: VerticalTierData;
    [tierId: string]: VerticalTierData;
  };
}

/**
 * Normalizes input slug from route parameters or props to canonical vertical key.
 */
export const canonicalVerticalSlug = (rawSlug?: string): string => {
  if (!rawSlug) return 'general';
  const clean = rawSlug.toLowerCase().trim().replace(/^\/industry\//, '').replace(/\/$/, '');

  const ALIAS_MAP: Record<string, string> = {
    'pg': 'hotel',
    'pg-hostel': 'hotel',
    'pg-hostel-management-software': 'hotel',
    'hostel': 'hotel',
    'hospitality': 'hotel',
    'health': 'healthcare',
    'clinic': 'healthcare',
    'hospital': 'healthcare',
    'school': 'education',
    'college': 'education',
    'coaching': 'education',
    'salon-spa': 'salon',
    'spa': 'salon',
    'beauty': 'salon',
    'gym-fitness': 'gym',
    'fitness': 'gym',
    'food': 'restaurant',
    'f-and-b': 'restaurant',
    'cafe': 'restaurant',
    'real-estate': 'real_estate',
    'realestate': 'real_estate',
    'property': 'real_estate',
    'car-dealer': 'car_dealer',
    'car_dealership': 'car_dealer',
    'automobile': 'car_dealer',
    'bike-dealer': 'bike_dealer',
    'two-wheeler': 'bike_dealer',
    'two_wheeler_dealer': 'bike_dealer',
    'transport': 'logistics',
    'shipping': 'logistics',
    'tourism': 'travel',
    'retail': 'ecommerce',
    'shop': 'ecommerce',
    'distribution': 'trading',
    'wholesale': 'trading',
    'builder': 'construction',
    'interior': 'construction',
    'factory': 'manufacturing',
    'arena-gaming': 'arena',
    'gaming': 'arena',
    'sports': 'arena',
    'study-abroad': 'study_abroad',
    'education_abroad': 'study_abroad',
    'immigration': 'study_abroad',
    'it-services': 'it_services',
    'software_services': 'it_services',
    'software': 'it_services',
    'other': 'general',
    'generic': 'general',
  };

  return ALIAS_MAP[clean] || clean;
};

/**
 * Standardized 20-Vertical Tier Limit Catalog.
 * Exact mirror of BR-138 proposed database seed.
 */
export const VERTICAL_TIER_LIMITS_CATALOG: Record<string, VerticalLimitsSchema> = {
  // 1. Hotel & Hospitality (including PG/Hostel)
  hotel: {
    metrics: [
      { key: 'beds', label: 'Beds', unit: 'bed', overage_key: 'bed_overage_monthly' },
      { key: 'rooms', label: 'Rooms', unit: 'room', overage_key: 'room_overage_monthly' },
      { key: 'locations', label: 'Properties', unit: 'property', overage_key: 'location_overage_monthly' },
    ],
    tiers: {
      explorer: {
        beds: 20, rooms: 15, locations: 1,
        feature_bullets: [
          '20 beds, 15 rooms (+₹75/extra bed)',
          '1 property (+₹500/extra property)',
          '5 team members, 1 company',
          'Monthly rent run & prorated billing',
          'Tenant KYC & digital agreement',
          'Basic dashboard & isolated DB',
        ],
      },
      officer: {
        beds: 100, rooms: 60, locations: 3,
        feature_bullets: [
          '100 beds, 60 rooms (+₹22.50/extra bed)',
          '3 properties (+₹400/extra property)',
          '20 team members, 2 companies',
          'Sub-metered electricity billing',
          'Automated WhatsApp rent reminders & payments',
          'Full 22 AI character access & voice control',
        ],
      },
      commander: {
        beds: 300, rooms: 180, locations: 15,
        feature_bullets: [
          '300 beds, 180 rooms (+₹18/extra bed)',
          '15 properties (+₹300/extra property)',
          '50 team members, 6 companies',
          'Multi-property central dashboard',
          'Advanced revenue management & room allocation',
          'AI Costing: 100 calls/week & priority support',
        ],
      },
      titan: {
        beds: 750, rooms: 450, locations: 35,
        feature_bullets: [
          '750 beds, 450 rooms (+₹15/extra bed)',
          '35 properties (+₹250/extra property)',
          '100 team members, 10 companies',
          'Enterprise chain management & OTA channel manager',
          'Custom integration & dedicated support manager',
          'ALL specialty addons & unlimited API access',
        ],
      },
    },
  },

  // 2. Healthcare
  healthcare: {
    metrics: [
      { key: 'patients', label: 'Active Patients', unit: 'patient' },
      { key: 'doctors', label: 'Doctors / Practitioners', unit: 'practitioner' },
      { key: 'facilities', label: 'Clinics / Facilities', unit: 'facility' },
    ],
    tiers: {
      explorer: {
        patients: 100, doctors: 2, facilities: 1,
        feature_bullets: [
          '100 active patients',
          '2 doctors / practitioners',
          '1 clinic facility',
          '5 team members, 1 company',
          'OPD digital prescription & billing',
          'Basic medical records & isolated DB',
        ],
      },
      officer: {
        patients: 1000, doctors: 10, facilities: 3,
        feature_bullets: [
          '1,000 active patients',
          '10 doctors / practitioners',
          '3 clinic facilities',
          '20 team members, 2 companies',
          'IPD, OPD & lab investigation management',
          'Digital Rx pad, WhatsApp reports & AI voice control',
        ],
      },
      commander: {
        patients: 5000, doctors: 30, facilities: 10,
        feature_bullets: [
          '5,000 active patients',
          '30 doctors / practitioners',
          '10 clinic facilities',
          '50 team members, 6 companies',
          'Full hospital management & OT scheduling',
          'AYUSH & clinical compliance suite & priority support',
        ],
      },
      titan: {
        patients: -1, doctors: -1, facilities: -1,
        feature_bullets: [
          'Unlimited active patients',
          'Unlimited doctors & clinical staff',
          '35+ hospital branches / facilities',
          '100 team members, 10 companies',
          'Multi-hospital EHR/EMR network & ABDM readiness',
          'Custom HL7/FHIR integrations & 24/7 dedicated support',
        ],
      },
    },
  },

  // 3. Education
  education: {
    metrics: [
      { key: 'students', label: 'Enrolled Students', unit: 'student' },
      { key: 'teachers', label: 'Faculty / Teachers', unit: 'teacher' },
      { key: 'campuses', label: 'Campuses / Branches', unit: 'campus' },
    ],
    tiers: {
      explorer: {
        students: 150, teachers: 5, campuses: 1,
        feature_bullets: [
          '150 enrolled students',
          '5 faculty / teachers',
          '1 campus / branch',
          '5 team members, 1 company',
          'Student admissions & digital attendance',
          'Basic fee collection & isolated DB',
        ],
      },
      officer: {
        students: 1000, teachers: 30, campuses: 3,
        feature_bullets: [
          '1,000 enrolled students',
          '30 faculty / teachers',
          '3 campuses / branches',
          '20 team members, 2 companies',
          'Automated fee collection, receipts & GST billing',
          'Exam grading, report cards & student/parent portal',
        ],
      },
      commander: {
        students: 3500, teachers: 100, campuses: 10,
        feature_bullets: [
          '3,500 enrolled students',
          '100 faculty / teachers',
          '10 campuses / branches',
          '50 team members, 6 companies',
          'Timetable optimization, transport & hostel management',
          'AI-driven performance analytics & priority support',
        ],
      },
      titan: {
        students: -1, teachers: -1, campuses: -1,
        feature_bullets: [
          'Unlimited enrolled students',
          'Unlimited faculty & staff',
          '35+ institutions / campuses',
          '100 team members, 10 companies',
          'University ERP & custom LMS integrations',
          'Dedicated account manager & 24/7 SLA',
        ],
      },
    },
  },

  // 4. Salon & Spa
  salon: {
    metrics: [
      { key: 'chairs', label: 'Styling Chairs', unit: 'chair' },
      { key: 'stylists', label: 'Stylists / Staff', unit: 'stylist' },
      { key: 'outlets', label: 'Outlets / Branches', unit: 'outlet' },
    ],
    tiers: {
      explorer: {
        chairs: 2, stylists: 3, outlets: 1,
        feature_bullets: [
          '2 styling chairs',
          '3 stylists / therapists',
          '1 outlet',
          '5 team members, 1 company',
          'Appointment scheduling & service catalog',
          'Quick POS billing & isolated DB',
        ],
      },
      officer: {
        chairs: 8, stylists: 10, outlets: 3,
        feature_bullets: [
          '8 styling chairs',
          '10 stylists / therapists',
          '3 outlets / salons',
          '20 team members, 2 companies',
          'Client memberships, prepaid wallets & package billing',
          'Staff commission tracking & WhatsApp appointment reminders',
        ],
      },
      commander: {
        chairs: 25, stylists: 35, outlets: 10,
        feature_bullets: [
          '25 styling chairs',
          '35 stylists / therapists',
          '10 outlets / salons',
          '50 team members, 6 companies',
          'Multi-branch retail inventory & consumables tracking',
          'Customer loyalty rewards & priority support',
        ],
      },
      titan: {
        chairs: -1, stylists: -1, outlets: -1,
        feature_bullets: [
          'Unlimited styling chairs & staff',
          '35+ salon & spa outlets',
          '100 team members, 10 companies',
          'Franchise chain management & central inventory replenishment',
          'White-label mobile client portal & dedicated manager',
        ],
      },
    },
  },

  // 5. Gym & Fitness
  gym: {
    metrics: [
      { key: 'members', label: 'Active Members', unit: 'member' },
      { key: 'trainers', label: 'Trainers / Instructors', unit: 'trainer' },
      { key: 'branches', label: 'Gym Branches', unit: 'branch' },
    ],
    tiers: {
      explorer: {
        members: 50, trainers: 2, branches: 1,
        feature_bullets: [
          '50 active members',
          '2 trainers / coaches',
          '1 gym branch',
          '5 team members, 1 company',
          'Membership passes & check-in logs',
          'Payment collection & isolated DB',
        ],
      },
      officer: {
        members: 300, trainers: 8, branches: 3,
        feature_bullets: [
          '300 active members',
          '8 trainers / coaches',
          '3 gym branches',
          '20 team members, 2 companies',
          'Custom workout & diet plans, locker allocations',
          'Biometric gate sync, automated recurring dues & WhatsApp alerts',
        ],
      },
      commander: {
        members: 1500, trainers: 25, branches: 10,
        feature_bullets: [
          '1,500 active members',
          '25 trainers / coaches',
          '10 gym branches',
          '50 team members, 6 companies',
          'Class scheduling (Yoga, CrossFit, Zumba) & PT revenue splits',
          'Supplement shop inventory & priority support',
        ],
      },
      titan: {
        members: -1, trainers: -1, branches: -1,
        feature_bullets: [
          'Unlimited active members & trainers',
          '35+ fitness clubs / branches',
          '100 team members, 10 companies',
          'Multi-city fitness chain management & member roaming pass',
          'Custom hardware turnstile integrations & dedicated SLA',
        ],
      },
    },
  },

  // 6. Restaurant & F&B
  restaurant: {
    metrics: [
      { key: 'tables', label: 'Dine-in Tables', unit: 'table' },
      { key: 'outlets', label: 'Outlets / Kitchens', unit: 'outlet' },
      { key: 'kot_screens', label: 'KOT Screens', unit: 'screen' },
    ],
    tiers: {
      explorer: {
        tables: 8, outlets: 1, kot_screens: 1,
        feature_bullets: [
          '8 dine-in tables',
          '1 restaurant outlet',
          '1 kitchen display / KOT printer',
          '5 team members, 1 company',
          'Menu management & QR code table ordering',
          'Split billing, GST invoice & isolated DB',
        ],
      },
      officer: {
        tables: 30, outlets: 3, kot_screens: 4,
        feature_bullets: [
          '30 dine-in tables',
          '3 restaurant outlets / cloud kitchens',
          'Multi-station Kitchen Display System (KDS)',
          '20 team members, 2 companies',
          'Recipe costing, ingredient inventory & low-stock alerts',
          'Captain mobile ordering & WhatsApp receipt dispatch',
        ],
      },
      commander: {
        tables: 100, outlets: 10, kot_screens: 15,
        feature_bullets: [
          '100 dine-in tables',
          '10 restaurant outlets / commissaries',
          '50 team members, 6 companies',
          'Central commissary kitchen & inter-branch transfer',
          'Zomato/Swiggy order aggregator sync & loyalty program',
          'AI food cost optimization & priority support',
        ],
      },
      titan: {
        tables: -1, outlets: -1, kot_screens: -1,
        feature_bullets: [
          'Unlimited dine-in tables & KDS stations',
          '35+ restaurant & cloud kitchen locations',
          '100 team members, 10 companies',
          'Enterprise QSR chain ERP & central recipe vault',
          'Custom POS hardware integrations & dedicated support',
        ],
      },
    },
  },

  // 7. Real Estate
  real_estate: {
    metrics: [
      { key: 'listings', label: 'Active Property Listings', unit: 'listing' },
      { key: 'agents', label: 'Agents / Brokers', unit: 'agent' },
      { key: 'branches', label: 'Offices / Branches', unit: 'branch' },
    ],
    tiers: {
      explorer: {
        listings: 25, agents: 2, branches: 1,
        feature_bullets: [
          '25 active property listings',
          '2 agents / brokers',
          '1 office branch',
          '5 team members, 1 company',
          'Buyer lead capture & site visit scheduling',
          'Property brochure generator & isolated DB',
        ],
      },
      officer: {
        listings: 150, agents: 10, branches: 3,
        feature_bullets: [
          '150 active property listings',
          '10 agents / brokers',
          '3 office branches',
          '20 team members, 2 companies',
          'Commission split tracker & deal closing pipeline',
          'SEAL digital e-signature agreements & client portal',
        ],
      },
      commander: {
        listings: 600, agents: 30, branches: 10,
        feature_bullets: [
          '600 active property listings',
          '30 agents / brokers',
          '10 office branches',
          '50 team members, 6 companies',
          'Developer project inventory management & unit booking lock',
          'Channel partner payouts & priority support',
        ],
      },
      titan: {
        listings: -1, agents: -1, branches: -1,
        feature_bullets: [
          'Unlimited property listings & agents',
          '35+ real estate offices / branches',
          '100 team members, 10 companies',
          'Large-scale real estate brokerage & developer ERP',
          'Custom MLS / property portal feed sync & dedicated manager',
        ],
      },
    },
  },

  // 8. Car Dealership
  car_dealer: {
    metrics: [
      { key: 'inventory', label: 'Vehicle Inventory Listings', unit: 'vehicle' },
      { key: 'showrooms', label: 'Showrooms', unit: 'showroom' },
    ],
    tiers: {
      explorer: {
        inventory: 15, showrooms: 1,
        feature_bullets: [
          '15 active vehicle listings',
          '1 dealership showroom',
          '5 team members, 1 company',
          'Test drive booking & lead capture',
          'Basic quotation & isolated DB',
        ],
      },
      officer: {
        inventory: 60, showrooms: 3,
        feature_bullets: [
          '60 active vehicle listings',
          '3 dealership showrooms',
          '20 team members, 2 companies',
          'Vehicle inspection, trade-in valuation & finance lead tracker',
          'Insurance renewal reminders & customer portal',
        ],
      },
      commander: {
        inventory: 250, showrooms: 10,
        feature_bullets: [
          '250 active vehicle listings',
          '10 dealership showrooms',
          '50 team members, 6 companies',
          'Multi-brand new & pre-owned inventory allocation',
          'Workshop job card sync & priority support',
        ],
      },
      titan: {
        inventory: -1, showrooms: -1,
        feature_bullets: [
          'Unlimited vehicle listings',
          '35+ dealership showrooms & service centers',
          '100 team members, 10 companies',
          'National auto dealer group ERP & OEM DMS integration',
          'Dedicated support manager & custom API feeds',
        ],
      },
    },
  },

  // 9. Bike Dealership
  bike_dealer: {
    metrics: [
      { key: 'inventory', label: 'Bike Inventory Listings', unit: 'bike' },
      { key: 'bays', label: 'Service Bays', unit: 'bay' },
      { key: 'outlets', label: 'Showroom Outlets', unit: 'outlet' },
    ],
    tiers: {
      explorer: {
        inventory: 20, bays: 2, outlets: 1,
        feature_bullets: [
          '20 bike listings (new / used / EV)',
          '2 service workshop bays',
          '1 dealership outlet',
          '5 team members, 1 company',
          'Digital job card creation & spare parts billing',
          'Basic sales register & isolated DB',
        ],
      },
      officer: {
        inventory: 80, bays: 6, outlets: 3,
        feature_bullets: [
          '80 bike listings',
          '6 service workshop bays',
          '3 dealership outlets',
          '20 team members, 2 companies',
          'Spare parts inventory, reorder level alerts & AMC packages',
          'Automated service due WhatsApp reminders & technician logs',
        ],
      },
      commander: {
        inventory: 300, bays: 18, outlets: 10,
        feature_bullets: [
          '300 bike listings',
          '18 service workshop bays',
          '10 dealership outlets',
          '50 team members, 6 companies',
          'Multi-location spare parts transfer & warranty claims tracking',
          'Accessory shop POS & priority support',
        ],
      },
      titan: {
        inventory: -1, bays: -1, outlets: -1,
        feature_bullets: [
          'Unlimited bike inventory & service bays',
          '35+ two-wheeler outlets',
          '100 team members, 10 companies',
          'Enterprise two-wheeler dealer network ERP',
          'Custom OEM integrations & dedicated support manager',
        ],
      },
    },
  },

  // 10. Logistics & Transport
  logistics: {
    metrics: [
      { key: 'fleet', label: 'Fleet Vehicles', unit: 'vehicle' },
      { key: 'shipments', label: 'Monthly Shipments', unit: 'shipment' },
      { key: 'warehouses', label: 'Warehouses / Hubs', unit: 'warehouse' },
    ],
    tiers: {
      explorer: {
        fleet: 3, shipments: 100, warehouses: 1,
        feature_bullets: [
          '3 fleet vehicles',
          '100 shipments / month',
          '1 warehouse / hub',
          '5 team members, 1 company',
          'Consignment note generation & e-Way bill tracker',
          'Basic freight invoice & isolated DB',
        ],
      },
      officer: {
        fleet: 15, shipments: 1000, warehouses: 3,
        feature_bullets: [
          '15 fleet vehicles',
          '1,000 shipments / month',
          '3 warehouses / hubs',
          '20 team members, 2 companies',
          'Trip dispatch, route planning & driver advances',
          'POD digital upload, shipment milestone tracking & customer portal',
        ],
      },
      commander: {
        fleet: 60, shipments: 5000, warehouses: 10,
        feature_bullets: [
          '60 fleet vehicles',
          '5,000 shipments / month',
          '10 warehouses / hubs',
          '50 team members, 6 companies',
          'Hub-and-spoke cargo manifest & cross-docking inventory',
          'Transporter billing, diesel expense analysis & priority support',
        ],
      },
      titan: {
        fleet: -1, shipments: -1, warehouses: -1,
        feature_bullets: [
          'Unlimited fleet vehicles & shipments',
          '35+ warehouses & transshipment hubs',
          '100 team members, 10 companies',
          'Multimodal logistics & 3PL freight forwarder ERP',
          'GPS telematics & Fastag API integration with dedicated SLA',
        ],
      },
    },
  },

  // 11. Travel & Tourism
  travel: {
    metrics: [
      { key: 'itineraries', label: 'Tour Itineraries / Packages', unit: 'itinerary' },
      { key: 'bookings', label: 'Monthly Bookings', unit: 'booking' },
      { key: 'branches', label: 'Agency Branches', unit: 'branch' },
    ],
    tiers: {
      explorer: {
        itineraries: 15, bookings: 50, branches: 1,
        feature_bullets: [
          '15 tour itineraries / packages',
          '50 bookings / month',
          '1 agency office',
          '5 team members, 1 company',
          'Day-wise itinerary builder & PDF quote generator',
          'Lead tracking & isolated DB',
        ],
      },
      officer: {
        itineraries: 80, bookings: 400, branches: 3,
        feature_bullets: [
          '80 tour itineraries / packages',
          '400 bookings / month',
          '3 agency offices',
          '20 team members, 2 companies',
          'Supplier voucher dispatch (hotels, cabs, guides) & cost tracking',
          'Client travel portal, visa checklist & flight voucher attachment',
        ],
      },
      commander: {
        itineraries: 300, bookings: 2000, branches: 10,
        feature_bullets: [
          '300 tour itineraries / packages',
          '2,000 bookings / month',
          '10 agency offices',
          '50 team members, 6 companies',
          'Group tour operator manager & seat departure planner',
          'B2B sub-agent portal & priority support',
        ],
      },
      titan: {
        itineraries: -1, bookings: -1, branches: -1,
        feature_bullets: [
          'Unlimited itineraries & bookings',
          '35+ agency branches',
          '100 team members, 10 companies',
          'Destination Management Company (DMC) enterprise suite',
          'GDS & supplier API synchronizations with dedicated support',
        ],
      },
    },
  },

  // 12. E-commerce & Retail
  ecommerce: {
    metrics: [
      { key: 'products', label: 'Catalog Products', unit: 'product' },
      { key: 'orders', label: 'Monthly Orders', unit: 'order' },
      { key: 'storefronts', label: 'Online Storefronts', unit: 'storefront' },
    ],
    tiers: {
      explorer: {
        products: 50, orders: 100, storefronts: 1,
        feature_bullets: [
          '50 catalog products',
          '100 orders / month',
          '1 online storefront',
          '5 team members, 1 company',
          'Integrated checkout & online payment gateway',
          'Order fulfillment tracking & isolated DB',
        ],
      },
      officer: {
        products: 500, orders: 1500, storefronts: 2,
        feature_bullets: [
          '500 catalog products',
          '1,500 orders / month',
          '2 online storefronts',
          '20 team members, 2 companies',
          'Discount coupons, customer accounts & wishlists',
          'Inventory batch tracking & automated GST invoice dispatch',
        ],
      },
      commander: {
        products: 5000, orders: 10000, storefronts: 5,
        feature_bullets: [
          '5,000 catalog products',
          '10,000 orders / month',
          '5 online storefronts',
          '50 team members, 6 companies',
          'Multi-warehouse stock sync & automated shipping labels',
          'Cart abandonment recovery & priority support',
        ],
      },
      titan: {
        products: -1, orders: -1, storefronts: 20,
        feature_bullets: [
          'Unlimited products & monthly orders',
          '20 custom branded storefronts',
          '100 team members, 10 companies',
          'High-volume omnichannel retail & marketplace integrations',
          'Custom ERP sync, dedicated servers & 24/7 SLA',
        ],
      },
    },
  },

  // 13. Trading & Distribution
  trading: {
    metrics: [
      { key: 'dealers', label: 'Channel Dealers / B2B Clients', unit: 'dealer' },
      { key: 'skus', label: 'Product SKUs', unit: 'sku' },
      { key: 'warehouses', label: 'Warehouses', unit: 'warehouse' },
    ],
    tiers: {
      explorer: {
        dealers: 5, skus: 100, warehouses: 1,
        feature_bullets: [
          '5 channel dealers / B2B accounts',
          '100 active SKUs',
          '1 warehouse',
          '5 team members, 1 company',
          'Wholesale price lists & GST invoicing',
          'Inventory balances & isolated DB',
        ],
      },
      officer: {
        dealers: 30, skus: 1000, warehouses: 3,
        feature_bullets: [
          '30 channel dealers / B2B accounts',
          '1,000 active SKUs',
          '3 warehouses',
          '20 team members, 2 companies',
          'Quantity slab pricing, credit limits & payment terms',
          'Sales rep order booking & dispatch manifest',
        ],
      },
      commander: {
        dealers: 150, skus: 5000, warehouses: 10,
        feature_bullets: [
          '150 channel dealers / B2B accounts',
          '5,000 active SKUs',
          '10 warehouses',
          '50 team members, 6 companies',
          'Sales territory management & B2B customer ordering portal',
          'Batch & expiry date tracking with priority support',
        ],
      },
      titan: {
        dealers: -1, skus: -1, warehouses: -1,
        feature_bullets: [
          'Unlimited channel dealers & SKUs',
          '35+ distribution centers & warehouses',
          '100 team members, 10 companies',
          'National wholesale supply chain & distributor network ERP',
          'Custom EDI/API integrations with dedicated account manager',
        ],
      },
    },
  },

  // 14. Construction
  construction: {
    metrics: [
      { key: 'projects', label: 'Active Projects', unit: 'project' },
      { key: 'tenders', label: 'Tenders / Cost Estimates', unit: 'tender' },
      { key: 'sites', label: 'Project Sites', unit: 'site' },
    ],
    tiers: {
      explorer: {
        projects: 2, tenders: 5, sites: 1,
        feature_bullets: [
          '2 active projects',
          '5 tender estimates',
          '1 construction site',
          '5 team members, 1 company',
          'BOQ material tracking & daily labor logs',
          'Subcontractor expense bills & isolated DB',
        ],
      },
      officer: {
        projects: 8, tenders: 25, sites: 3,
        feature_bullets: [
          '8 active projects',
          '25 tender estimates',
          '3 construction sites',
          '20 team members, 2 companies',
          'Work orders, material issue slips & site store inventory',
          'Client milestone RA bills & payment stage tracker',
        ],
      },
      commander: {
        projects: 25, tenders: 100, sites: 10,
        feature_bullets: [
          '25 active projects',
          '100 tender estimates',
          '10 construction sites',
          '50 team members, 6 companies',
          'CPWD / State DSR rate analysis & tender NIT extraction',
          'Equipment maintenance tracker & priority support',
        ],
      },
      titan: {
        projects: -1, tenders: -1, sites: -1,
        feature_bullets: [
          'Unlimited construction projects & tender bids',
          '35+ project job sites',
          '100 team members, 10 companies',
          'Enterprise infrastructure contractor & EPC management ERP',
          'Custom financial accounting & engineering software integrations',
        ],
      },
    },
  },

  // 15. Manufacturing
  manufacturing: {
    metrics: [
      { key: 'work_centers', label: 'Work Centers / Lines', unit: 'work_center' },
      { key: 'orders', label: 'Monthly Production Orders', unit: 'order' },
      { key: 'warehouses', label: 'Plant Warehouses', unit: 'warehouse' },
    ],
    tiers: {
      explorer: {
        work_centers: 2, orders: 50, warehouses: 1,
        feature_bullets: [
          '2 work centers / production lines',
          '50 production orders / month',
          '1 plant warehouse',
          '5 team members, 1 company',
          'Multi-level Bill of Materials (BOM)',
          'Raw material consumption & isolated DB',
        ],
      },
      officer: {
        work_centers: 8, orders: 300, warehouses: 3,
        feature_bullets: [
          '8 work centers / production lines',
          '300 production orders / month',
          '3 plant warehouses',
          '20 team members, 2 companies',
          'Job card stage tracking, scrap reporting & machine downtime logs',
          'Finished goods costing & quality inspection checkpoints',
        ],
      },
      commander: {
        work_centers: 25, orders: 1500, warehouses: 10,
        feature_bullets: [
          '25 work centers / production lines',
          '1,500 production orders / month',
          '10 plant warehouses',
          '50 team members, 6 companies',
          'Material Requirements Planning (MRP) & production scheduling',
          'Subcontracting work order tracking & priority support',
        ],
      },
      titan: {
        work_centers: -1, orders: -1, warehouses: -1,
        feature_bullets: [
          'Unlimited work centers & production orders',
          '35+ factories & regional warehouses',
          '100 team members, 10 companies',
          'Enterprise discrete & process manufacturing ERP',
          'SCADA / IoT machine logging integration & dedicated support',
        ],
      },
    },
  },

  // 16. Arena & Gaming
  arena: {
    metrics: [
      { key: 'stations', label: 'Stations / Tables / Lanes', unit: 'station' },
      { key: 'arenas', label: 'Venues / Arenas', unit: 'venue' },
    ],
    tiers: {
      explorer: {
        stations: 4, arenas: 1,
        feature_bullets: [
          '4 gaming stations / snooker tables',
          '1 gaming venue',
          '5 team members, 1 company',
          'Hourly slot timer & automatic light controller log',
          'Snack bar quick billing & isolated DB',
        ],
      },
      officer: {
        stations: 15, arenas: 3,
        feature_bullets: [
          '15 gaming stations / snooker tables / turf lanes',
          '3 gaming venues / sports arenas',
          '20 team members, 2 companies',
          'Membership passes, prepaid play balance & loyalty points',
          'Tournament bracket management & scoreboard display',
        ],
      },
      commander: {
        stations: 50, arenas: 10,
        feature_bullets: [
          '50 gaming stations / tables / turf grounds',
          '10 gaming venues / sports arenas',
          '50 team members, 6 companies',
          'Esports league organizer & turf slot booking mobile portal',
          'Staff shift billing auditing & priority support',
        ],
      },
      titan: {
        stations: -1, arenas: -1,
        feature_bullets: [
          'Unlimited gaming stations & sports lanes',
          '35+ sports & esports entertainment centers',
          '100 team members, 10 companies',
          'National gaming franchise ERP & central loyalty points network',
          'Custom hardware controller integrations & dedicated manager',
        ],
      },
    },
  },

  // 17. Study Abroad
  study_abroad: {
    metrics: [
      { key: 'applicants', label: 'Active Student Applicants', unit: 'applicant' },
      { key: 'counsellors', label: 'Counsellors', unit: 'counsellor' },
      { key: 'offices', label: 'Offices / Branches', unit: 'office' },
    ],
    tiers: {
      explorer: {
        applicants: 25, counsellors: 2, offices: 1,
        feature_bullets: [
          '25 active student applicants',
          '2 study abroad counsellors',
          '1 office branch',
          '5 team members, 1 company',
          'Country & course search with document checklist',
          'Application status pipeline & isolated DB',
        ],
      },
      officer: {
        applicants: 150, counsellors: 8, offices: 3,
        feature_bullets: [
          '150 active student applicants',
          '8 study abroad counsellors',
          '3 office branches',
          '20 team members, 2 companies',
          'University application dispatch & offer letter tracking',
          'Visa file checklist, test score vault & student portal',
        ],
      },
      commander: {
        applicants: 600, counsellors: 25, offices: 10,
        feature_bullets: [
          '600 active student applicants',
          '25 study abroad counsellors',
          '10 office branches',
          '50 team members, 6 companies',
          'University commission invoicing & intake pipeline forecasting',
          'Sub-agent referral payouts & priority support',
        ],
      },
      titan: {
        applicants: -1, counsellors: -1, offices: -1,
        feature_bullets: [
          'Unlimited student applicants & counsellors',
          '35+ overseas education branch offices',
          '100 team members, 10 companies',
          'Global education consultancy ERP & partner university sync',
          'Custom document OCR & dedicated account manager',
        ],
      },
    },
  },

  // 18. Consulting
  consulting: {
    metrics: [
      { key: 'clients', label: 'Active Client Accounts', unit: 'client' },
      { key: 'projects', label: 'Billable Projects', unit: 'project' },
      { key: 'staff', label: 'Consultants / Staff', unit: 'staff' },
    ],
    tiers: {
      explorer: {
        clients: 5, projects: 3, staff: 5,
        feature_bullets: [
          '5 active client accounts',
          '3 billable consulting projects',
          '5 team members, 1 company',
          'Timesheet tracking & retainer invoicing',
          'Client document vault & isolated DB',
        ],
      },
      officer: {
        clients: 25, projects: 15, staff: 20,
        feature_bullets: [
          '25 active client accounts',
          '15 billable consulting projects',
          '20 consultants / team members',
          '2 companies',
          'Milestone billing, expense reimbursement & client approval portal',
          'SEAL digital contract signing & automated NDA generation',
        ],
      },
      commander: {
        clients: 100, projects: 50, staff: 50,
        feature_bullets: [
          '100 active client accounts',
          '50 billable consulting projects',
          '50 consultants / team members',
          '6 companies',
          'Consultant utilization metrics & project margin analysis',
          'Multi-currency client billing & priority support',
        ],
      },
      titan: {
        clients: -1, projects: -1, staff: -1,
        feature_bullets: [
          'Unlimited client accounts & active projects',
          '100+ consultants across global practices',
          '10 companies',
          'Enterprise professional services automation (PSA) ERP',
          'Custom ERP integrations & dedicated engagement manager',
        ],
      },
    },
  },

  // 19. IT Services
  it_services: {
    metrics: [
      { key: 'clients', label: 'Active Client Accounts', unit: 'client' },
      { key: 'projects', label: 'Active Projects / Sprints', unit: 'project' },
      { key: 'devs', label: 'Developers / Staff', unit: 'developer' },
    ],
    tiers: {
      explorer: {
        clients: 5, projects: 3, devs: 5,
        feature_bullets: [
          '5 active client accounts',
          '3 active dev projects / sprints',
          '5 developers / staff, 1 company',
          'Sprint backlog, task boards & bug tracking',
          'Timesheet logger & isolated DB',
        ],
      },
      officer: {
        clients: 25, projects: 15, devs: 20,
        feature_bullets: [
          '25 active client accounts',
          '15 active dev projects / sprints',
          '20 developers / staff, 2 companies',
          'Git commit links, sprint velocity charts & client review portal',
          'Automated monthly developer retainer invoicing',
        ],
      },
      commander: {
        clients: 100, projects: 50, devs: 50,
        feature_bullets: [
          '100 active client accounts',
          '50 active dev projects / sprints',
          '50 developers / staff, 6 companies',
          'SLA response monitoring & developer resource forecasting',
          'Time & Material (T&M) automated billing & priority support',
        ],
      },
      titan: {
        clients: -1, projects: -1, devs: -1,
        feature_bullets: [
          'Unlimited client accounts & engineering projects',
          '100+ software engineers across delivery centers',
          '10 companies',
          'Enterprise software services delivery & IT staff augmentation ERP',
          'Dedicated account director & 24/7 SLA',
        ],
      },
    },
  },

  // 20. General / Platform Operating System (Fallback & Main Pricing Page)
  general: {
    metrics: [
      { key: 'companies', label: 'Companies', unit: 'company' },
      { key: 'employees', label: 'Team Members', unit: 'employee' },
      { key: 'storage_gb', label: 'Cloud Storage', unit: 'storage' },
    ],
    tiers: {
      explorer: {
        companies: 1, employees: 5, storage_gb: 5,
        feature_bullets: [
          '5 team members, 1 company',
          'Up to 1 industry vertical',
          '5 GB cloud storage',
          'Email Suite (3 accounts)',
          'Isolated MySQL tenant database',
          'HYPERION AI assistant (limited)',
          'SEAL E-Signatures & REMEDY Complaints',
          'Basic dashboard & standard analytics view',
        ],
      },
      officer: {
        companies: 2, employees: 20, storage_gb: 25,
        feature_bullets: [
          '20 team members, 2 companies',
          'Up to 2 industry verticals',
          '25 GB cloud storage',
          'Email Suite (5 accounts)',
          'Full 22 AI character access',
          'Voice control (58,000+ commands)',
          'Multi-language (81 languages)',
          'Storefront & website builder unlocked',
        ],
      },
      commander: {
        companies: 6, employees: 50, storage_gb: 50,
        feature_bullets: [
          '50 team members, 6 companies',
          'Up to 5 industry verticals',
          '50 GB cloud storage',
          'Email Suite (20 accounts)',
          'AI Costing: 100 calls/week',
          'All storefront themes unlocked',
          'Advanced analytics & multi-company console',
          'Priority support',
        ],
      },
      titan: {
        companies: 10, employees: 100, storage_gb: 200,
        feature_bullets: [
          '100 team members, 10 companies',
          'Up to 10 industry verticals',
          '200 GB cloud storage',
          'Email Suite (50 accounts)',
          'AI Costing: 300 calls/week',
          'ALL Specialty Addons included',
          'Unlimited API calls & white-label options',
          'Dedicated account manager & 24/7 phone support',
        ],
      },
    },
  },
};

/**
 * Resolves the feature bullet array to render inside a PricingCard.
 *
 * Precedence:
 * 1. If verticalSlug is specified:
 *    a. If live Mothership API provides `vertical_limits.tiers[tier.id].feature_bullets`, use those live bullets.
 *    b. Otherwise, use `VERTICAL_TIER_LIMITS_CATALOG[canonicalSlug].tiers[tier.id].feature_bullets`.
 *    c. Automatically prepend the "Everything in X +" upgrade highlight for non-free tiers.
 * 2. If NO verticalSlug is specified (e.g. main `/pricing` page):
 *    a. Cleans out any PG bed/room leaks from tier features.
 *    b. Uses clean business OS bullets from `VERTICAL_TIER_LIMITS_CATALOG.general`.
 */
export const resolveTierFeatures = (
  tier: PricingTier,
  verticalSlug?: string,
  liveVerticalLimits?: Record<string, any> | null
): PricingFeature[] => {
  const isVerticalSpecific = Boolean(verticalSlug && verticalSlug.trim().length > 0);
  const canonical = canonicalVerticalSlug(verticalSlug);

  // Upgrade highlight label for paid tiers
  const PREV_LABEL: Record<string, string> = {
    officer: 'Explorer',
    commander: 'Officer',
    titan: 'Commander',
  };

  let rawBullets: string[] = [];

  if (isVerticalSpecific) {
    // 1. Try reading from live Mothership API vertical_limits
    const liveTiers = liveVerticalLimits?.tiers;
    const liveTierBullets = liveTiers?.[tier.id]?.feature_bullets;

    if (Array.isArray(liveTierBullets) && liveTierBullets.length > 0) {
      rawBullets = [...liveTierBullets];
    } else {
      // 2. Use standardized 20-vertical fallback catalog
      const verticalConfig = VERTICAL_TIER_LIMITS_CATALOG[canonical] || VERTICAL_TIER_LIMITS_CATALOG.general;
      const catalogTier = verticalConfig.tiers[tier.id] || verticalConfig.tiers.explorer;
      rawBullets = [...(catalogTier.feature_bullets || [])];
    }
  } else {
    // General pricing page: render pure business OS bullets
    const generalConfig = VERTICAL_TIER_LIMITS_CATALOG.general;
    const catalogTier = generalConfig.tiers[tier.id] || generalConfig.tiers.explorer;
    rawBullets = [...(catalogTier.feature_bullets || [])];
  }

  // Build PricingFeature array
  const output: PricingFeature[] = [];

  // Prepend upgrade highlight for paid tiers if not already present
  const prevTierName = PREV_LABEL[tier.id];
  if (prevTierName) {
    output.push({
      text: `Everything in ${prevTierName} +`,
      included: true,
      highlight: true,
    });
  }

  for (const bulletText of rawBullets) {
    // Prevent duplicate highlight bullet
    if (bulletText.toLowerCase().includes('everything in')) continue;

    output.push({
      text: bulletText,
      included: true,
    });
  }

  return output;
};
